import { reportClientError } from '~/composables/useErrorReporter'

export default defineNuxtPlugin((nuxtApp) => {
  if (typeof window === 'undefined') return

  const recentKeys = new Map<string, number>()
  const DEDUPE_WINDOW_MS = 5000
  /** Les stacks profondes (ex. récursion) explosent la taille de la Map de dédoublonnage. */
  const DEDUPE_KEY_MAX_LEN = 512

  function dedupeKey(parts: Array<string | number | null | undefined>): string {
    const raw = parts.map((p) => (p == null ? '' : String(p))).join(':')
    return raw.length > DEDUPE_KEY_MAX_LEN ? raw.slice(0, DEDUPE_KEY_MAX_LEN) : raw
  }

  function shouldReport(key: string) {
    const now = Date.now()
    const last = recentKeys.get(key) ?? 0
    if (now - last < DEDUPE_WINDOW_MS) return false
    recentKeys.set(key, now)
    if (recentKeys.size > 200) {
      for (const [k, ts] of recentKeys) {
        if (now - ts > DEDUPE_WINDOW_MS * 2) recentKeys.delete(k)
      }
    }
    return true
  }

  window.addEventListener('error', (event) => {
    const stack = event.error instanceof Error ? event.error.stack : null
    const rawMessage = event.message || 'Unhandled window error'
    // Browsers often hide cross-origin script failures as generic "Script error."
    const isOpaqueScriptError = rawMessage.toLowerCase() === 'script error.'
    const kind = isOpaqueScriptError ? 'window_error_opaque' : 'window_error'
    const key = dedupeKey([kind, rawMessage, event.filename ?? 'none', event.lineno ?? 0, event.colno ?? 0])
    if (!shouldReport(key)) return

    void reportClientError({
      kind,
      message: rawMessage,
      stack,
      meta: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        href: window.location.href,
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
      },
    })
  })

  // Resource loading failures (script/css/img) are emitted as Event, not ErrorEvent.
  window.addEventListener(
    'error',
    (event) => {
      const target = event.target as HTMLElement | null
      if (!target) return
      const tag = target.tagName?.toLowerCase()
      if (!tag || (tag !== 'script' && tag !== 'link' && tag !== 'img')) return
      const source =
        (target as HTMLScriptElement | HTMLImageElement).src ||
        (target as HTMLLinkElement).href ||
        null
      // External sprite host can occasionally miss specific assets; track via gameplay telemetry, not P0 errors.
      if (tag === 'img' && typeof source === 'string' && source.includes('play.pokemonshowdown.com/sprites/')) {
        return
      }
      const key = dedupeKey(['resource_error', tag, source ?? 'unknown'])
      if (!shouldReport(key)) return

      void reportClientError({
        kind: 'resource_error',
        message: `Resource failed to load: ${tag}`,
        meta: {
          tag,
          source,
          href: window.location.href,
          online: typeof navigator !== 'undefined' ? navigator.onLine : null,
        },
      })
    },
    true
  )

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : 'Unhandled promise rejection'
    const stack = reason instanceof Error ? reason.stack : null
    // Les rejets type "Maximum call stack size exceeded" embarquent une stack énorme (90+ frames).
    // On ne garde que le début pour la clé de dédoublonnage afin d'éviter de saturer la Map.
    const stackHead = stack ? stack.slice(0, 240) : 'nostack'
    const key = dedupeKey(['unhandled_rejection', message, stackHead])
    if (!shouldReport(key)) return

    void reportClientError({
      kind: 'unhandled_rejection',
      message,
      stack,
    })
  })

  nuxtApp.vueApp.config.errorHandler = (error, instance, info) => {
    const message = error instanceof Error ? error.message : String(error)
    const stack = error instanceof Error ? error.stack : null
    const instanceType = (instance as { type?: { name?: unknown } } | null)?.type
    const componentName =
      instanceType && typeof instanceType === 'object' && typeof instanceType.name === 'string'
        ? instanceType.name
        : null
    const key = dedupeKey(['vue_error', message, componentName ?? 'unknown', info])
    if (!shouldReport(key)) return

    void reportClientError({
      kind: 'vue_error',
      message,
      stack,
      meta: {
        info,
        componentName,
      },
    })
  }
})
